import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, MailCheck, MailWarning, ArrowRight, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { AuthLayout } from "./AuthLayout";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../data/authStore";

const useQuery = () => new URLSearchParams(useLocation().search);

/**
 * VerifyEmailPage — public route at /verify-email[?token=…]
 *
 * Two modes:
 *   • token in URL → confirm immediately, swap JWT, route to dashboard.
 *   • no token    → show "check your inbox" gate with resend CTA. This is
 *                    what soft-block routes the user to when /api/* returns
 *                    {code: 'email_unverified'}.
 */
export default function VerifyEmailPage() {
  const q = useQuery();
  const token = q.get("token") || "";
  const nav = useNavigate();
  const { user, refresh, logout } = useAuth();

  const [state, setState] = useState(token ? "verifying" : "idle"); // idle | verifying | success | error
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/api/auth/verify-email", { token });
        if (cancelled) return;
        await refresh?.();
        setState("success");
        toast.success("Email verified", { description: "Welcome to ChainFlow." });
        setTimeout(() => nav("/today", { replace: true }), 900);
      } catch (err) {
        if (cancelled) return;
        setError(apiError(err) || "This verification link is invalid or expired.");
        setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token, nav, refresh]);

  const resend = async () => {
    if (!user?.email) {
      toast.error("Sign in first so we know which inbox to send to.");
      return;
    }
    setResending(true);
    try {
      await api.post("/api/auth/resend-verification", { email: user.email });
      toast.success("Verification email sent", { description: "Check your inbox (and spam folder)." });
    } catch (err) {
      toast.error("Couldn't resend", { description: apiError(err) });
    } finally {
      setResending(false);
    }
  };

  if (state === "verifying") {
    return (
      <AuthLayout testid="verify-email-loading" eyebrow="Email verification" title="Verifying your email…" subtitle="One moment.">
        <div className="flex items-center justify-center py-10 text-slate-500 text-[13px]">
          <Loader2 size={16} className="animate-spin mr-2" /> Confirming…
        </div>
      </AuthLayout>
    );
  }

  if (state === "success") {
    return (
      <AuthLayout testid="verify-email-success" eyebrow="Email verified" title="You're all set" subtitle="Routing you to your workspace…">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-3">
          <MailCheck size={16} className="text-emerald-600 mt-0.5" />
          <p className="text-[12.5px] text-emerald-800 leading-snug">Email verified. Welcome aboard.</p>
        </div>
      </AuthLayout>
    );
  }

  if (state === "error") {
    return (
      <AuthLayout
        testid="verify-email-error"
        eyebrow="Email verification"
        title="Link is invalid or expired"
        subtitle="Verification links expire after 24 hours. Get a fresh one below or sign in to request another."
        footer={<>Back to <Link to="/login" className="font-semibold text-indigo-700 hover:text-indigo-800">sign in →</Link></>}
      >
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-3 mb-4">
          <MailWarning size={16} className="text-rose-600 mt-0.5" />
          <p className="text-[12.5px] text-rose-800 leading-snug" data-testid="verify-error-msg">{error}</p>
        </div>
        {user?.email && (
          <button
            type="button"
            onClick={resend}
            disabled={resending}
            className={`btn btn-primary !h-11 !px-5 !text-[13.5px] w-full ${resending ? "opacity-60 cursor-not-allowed !shadow-none" : ""}`}
            data-testid="verify-resend"
          >
            {resending ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><RefreshCcw size={14} /> Send a fresh link to {user.email}</>}
          </button>
        )}
      </AuthLayout>
    );
  }

  // idle — soft-block landing page after register or 403 redirect
  return (
    <AuthLayout
      testid="verify-email-gate"
      eyebrow="Verify your email"
      title="Check your inbox"
      subtitle={user?.email
        ? `We sent a verification link to ${user.email}. Click it to unlock the app.`
        : "We sent a verification link to your email. Click it to unlock the app."}
      footer={
        <>
          Wrong email? <button type="button" onClick={() => { logout?.(); nav("/login", { replace: true }); }} className="font-semibold text-indigo-700 hover:text-indigo-800" data-testid="verify-switch-account">
            Sign out and use another →
          </button>
        </>
      }
    >
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 px-4 py-3 flex items-start gap-3 mb-4" data-testid="verify-gate-card">
        <MailCheck size={16} className="text-indigo-700 mt-0.5" />
        <div className="text-[12.5px] text-indigo-900 leading-snug">
          We just sent you a one-click link. Verifying takes about ten seconds — then your dashboard is ready.
        </div>
      </div>

      <button
        type="button"
        onClick={resend}
        disabled={resending}
        className={`btn btn-primary !h-11 !px-5 !text-[13.5px] w-full ${resending ? "opacity-60 cursor-not-allowed !shadow-none" : ""}`}
        data-testid="verify-resend-btn"
      >
        {resending ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><RefreshCcw size={14} /> Resend verification email</>}
      </button>
      <p className="text-[11px] text-slate-400 mt-3 text-center leading-snug">
        Didn't get it? Check your spam folder or click resend above.
      </p>
    </AuthLayout>
  );
}
